import React, { useContext, createContext, useEffect, useMemo } from 'react';
import { TradingStats } from '@server/data-feed/schemas/output.schema';
import { useChartFeed } from '../chart/hooks/use-chart-feed';
import {
  createChartUpdateStream,
  ChartUpdateStream
} from '../chart/services/chart-update-stream.service';
import { useLayoutContext } from './layout-context';
import { TradingFormState } from '../trading-form/trading-form.types';
import { Observable } from 'rxjs';

type SymbolFeedContextValue = {
  tradingStats$: Observable<TradingStats | undefined>;
  loading$: Observable<boolean>;
  state: TradingFormState | null;
  chartId: string;
  chartUpdateStream: ChartUpdateStream;
};

const SymbolFeedContext = createContext<SymbolFeedContextValue | undefined>(
  undefined
);

export const SymbolFeedProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const { state: layoutState, chartId } = useLayoutContext();
  const state = layoutState as TradingFormState;

  const chartUpdateStream = useMemo(() => createChartUpdateStream(), []);

  useEffect(() => {
    return () => {
      chartUpdateStream.dispose();
    };
  }, [chartUpdateStream]);

  const { loading$ } = useChartFeed({
    formState: state,
    id: chartId,
    chartUpdateStream
  });
  const tradingStats$ = useMemo(
    () => chartUpdateStream.getTradingStats$(),
    [chartUpdateStream]
  );

  const value = useMemo(
    () => ({
      tradingStats$,
      loading$,
      state,
      chartId,
      chartUpdateStream
    }),
    [tradingStats$, loading$, state, chartId, chartUpdateStream]
  );

  return (
    <SymbolFeedContext.Provider value={value}>
      {children}
    </SymbolFeedContext.Provider>
  );
};

export const useSymbolFeedContext = () => {
  const context = useContext(SymbolFeedContext);
  if (!context) {
    throw new Error(
      'useSymbolFeedContext must be used within SymbolFeedProvider'
    );
  }

  return context;
};
